/**
 * Angled ends (claude-docs/2026-10-30-venus-full-line-design.md, group 6): a
 * bay's front (`type: 'angled-end'`) at a run's open end, its outer front
 * corner cut at 45° so the run tapers back instead of ending square — the one
 * rule the panel, the fill, the drawing, the counter and the SKU read.
 *
 *   - base — Venus `BEC24`, "Angled End Base", 24" x 34½" x 24": the corner
 *     cut 12" x 12", so its exposed end is 12" deep; a full-height door on
 *     the 12" of straight front and one on the angle;
 *   - wall — `WAE1230` / `WAE1236` / `WAE1242`, "Corner Wall - Angle End",
 *     12" x 12", 30" / 36" / 42" tall: three-sided, its one door on the
 *     diagonal (catalog p.13).
 *
 * The user (2026-11-01: "1. yes. 2. yes."): offered on the bay at a run's
 * open end, like the end shelf, and the counter over a base one cut at 45° to
 * follow its face. Its OPEN side is the side no cabinet stands against
 * (`endShelves.endShelfSideFor`), stamped by the fill (`angledEndSide`). The
 * bay stays a box to the packer and the collision rules; the drawing, the
 * plan outline and the counter follow the cut. A generic line offers the
 * same sizes.
 *
 * Pure; metres.
 */
const { endShelfSideFor, neighbourSides } = require('./endShelves');

const IN = 0.0254;
const TOL_IN = 1 / 16;

const ANGLED_ENDS = Object.freeze({
  base: Object.freeze({
    widthIn: 24,
    depthIn: 24,
    cutIn: 12,
    name: 'angled end base',
    kind: 'base-angled-end',
  }),
  wall: Object.freeze({
    widthIn: 12,
    depthIn: 12,
    cutIn: 12,
    heightsIn: Object.freeze([30, 36, 42]),
    name: 'angled end wall cabinet',
    kind: 'wall-angled-end',
  }),
});

/** Is this cabinet an angled end? */
const isAngledEnd = (o) => o?.category?.angledEnd === true;

/** The tier a bay is in: an upper's is the wall. */
const tierOf = (o) =>
  o?.type === 'upper-cabinet' || o?.category?.['upper-cabinet']
    ? 'wall'
    : 'base';

const said = (m) => `${Math.round((m / IN) * 8) / 8}"`;
const listText = (xs) =>
  xs.length > 1
    ? `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`
    : xs.join('');
const articled = (name) => `${/^[aeiou]/.test(name) ? 'An' : 'A'} ${name}`;

/**
 * Why this bay cannot be an angled end, or null when it can: its tier's
 * width, a base 24" deep, a wall one 30", 36" or 42" tall, and at an end of
 * its run (`objects`, the room's, find its neighbours; without them the end
 * goes unasked).
 */
function angledEndReason(run, cabinet, objects) {
  const d = cabinet?.dimensions;
  if (!(d?.x > 0)) return null;
  const spec = ANGLED_ENDS[tierOf(cabinet)];
  const name = articled(spec.name);
  if (Math.abs(d.x / IN - spec.widthIn) > TOL_IN)
    return `${name} is ${spec.widthIn}" wide: this bay is ${said(d.x)}`;
  if (spec.heightsIn) {
    if (!spec.heightsIn.some((h) => Math.abs(h - d.y / IN) <= 0.5))
      return `${name} is ${listText(spec.heightsIn.map((h) => `${h}"`))} tall: this bay is ${said(d.y)}`;
  } else if (d.z > 0 && Math.abs(d.z / IN - spec.depthIn) > 1)
    return `${name} is ${spec.depthIn}" deep: this bay is ${said(d.z)}`;
  if (objects) {
    const sides = neighbourSides(cabinet, objects);
    if (sides.left && sides.right)
      return `${name} closes a run’s end: this bay has cabinets on both sides`;
    if (!sides.left && !sides.right)
      return `${name} closes a run’s end against its last cabinet: this bay stands alone`;
  }
  return null;
}

/**
 * Stamp each angled end among `objects` with the side it stands open on
 * (`angledEndSide`), from what stands beside it among the same objects — the
 * fill's last pass, so the drawing and the counter read it off the cabinet.
 * One with no single open side carries none and is drawn square. Mutates the
 * angled ends only.
 */
function stampAngledEndSides(objects) {
  for (const o of objects) {
    if (!isAngledEnd(o)) continue;
    const side = endShelfSideFor(o, objects);
    if (side) o.angledEndSide = side;
    else delete o.angledEndSide;
  }
}

/**
 * An angled end's cut in its own frame (origin at its centre, +X to the
 * viewer's right facing it, +Z toward the room): the open side, how far the
 * cut runs back along each face, the two ends of the angled face and its
 * outward normal. Null for a box with no open side to cut.
 */
function angledEndCut(cabinet) {
  const side = cabinet?.angledEndSide;
  if (!isAngledEnd(cabinet) || (side !== 'left' && side !== 'right'))
    return null;
  const { x: w, z: d } = cabinet.dimensions;
  const cut = Math.min(ANGLED_ENDS[tierOf(cabinet)].cutIn * IN, w, d);
  const s = side === 'right' ? 1 : -1;
  return {
    side,
    cutM: cut,
    // on the front, `cut` in from the open side; on the open side, `cut`
    // back from the front
    front: { x: s * (w / 2 - cut), z: d / 2 },
    end: { x: (s * w) / 2, z: d / 2 - cut },
    normal: { x: s * Math.SQRT1_2, z: Math.SQRT1_2 },
  };
}

const near = (a, b) => Math.hypot(a.x - b.x, a.z - b.z) < 1e-6;

/**
 * An angled end's plan outline in its own frame, counter-clockwise seen from
 * above with +Z down the screen — the box with its open front corner cut: a
 * base's five corners, a wall one's three. The box's own four for one with
 * no cut.
 */
function angledEndLocalOutline(cabinet) {
  const { x: w, z: d } = cabinet.dimensions;
  const box = [
    { x: -w / 2, z: -d / 2 },
    { x: -w / 2, z: d / 2 },
    { x: w / 2, z: d / 2 },
    { x: w / 2, z: -d / 2 },
  ];
  const cut = angledEndCut(cabinet);
  if (!cut) return box;
  const corner = cut.side === 'right' ? 2 : 1;
  const pts =
    cut.side === 'right' ? [cut.front, cut.end] : [cut.end, cut.front];
  const out = [...box.slice(0, corner), ...pts, ...box.slice(corner + 1)];
  return out.filter((p, i) => !near(p, out[(i + out.length - 1) % out.length]));
}

/**
 * `angledEndLocalOutline` with its FRONT faces — the straight front and the
 * angled face — moved back by `by` (a door's thickness for the carcass behind
 * the doors, the toe-kick setback for the kick). Its own frame.
 */
function angledEndInset(cabinet, by) {
  const cut = angledEndCut(cabinet);
  const d = cabinet.dimensions.z;
  let out = clipToHalfPlane(
    angledEndLocalOutline(cabinet),
    (p) => p.z - (d / 2 - by)
  );
  if (cut)
    out = clipToHalfPlane(
      out,
      (p) =>
        (p.x - cut.front.x) * cut.normal.x +
        (p.z - cut.front.z) * cut.normal.z +
        by
    );
  return out;
}

/** A point in a cabinet's own frame, in the world's plan. */
function toWorld(cabinet, p) {
  const ry = cabinet.rotation?.y || 0;
  const c = Math.cos(ry);
  const s = Math.sin(ry);
  return {
    x: cabinet.center.x + p.x * c + p.z * s,
    z: cabinet.center.z - p.x * s + p.z * c,
  };
}

/** `angledEndLocalOutline` in the world's plan. */
const angledEndOutline = (cabinet) =>
  angledEndLocalOutline(cabinet).map((p) => toWorld(cabinet, p));

/** A rectangle object's four plan corners (its `polygonCorners` if it has them). */
function planCorners(o) {
  if (Array.isArray(o.polygonCorners) && o.polygonCorners.length >= 3)
    return o.polygonCorners.map((p) => ({ x: p.x, z: p.z }));
  const { x: w, z: d } = o.dimensions;
  return [
    { x: -w / 2, z: -d / 2 },
    { x: -w / 2, z: d / 2 },
    { x: w / 2, z: d / 2 },
    { x: w / 2, z: -d / 2 },
  ].map((p) => toWorld(o, p));
}

/** Clip a polygon to the points where `f(p) <= 0` (one Sutherland-Hodgman pass). */
function clipToHalfPlane(poly, f) {
  const out = [];
  for (let i = 0; i < poly.length; i += 1) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const fa = f(a);
    const fb = f(b);
    if (fa <= 0) out.push(a);
    if ((fa < 0 && fb > 0) || (fa > 0 && fb < 0)) {
      const t = fa / (fa - fb);
      out.push({ x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t });
    }
  }
  return out;
}

/**
 * The run's counter over a base angled end, cut at 45° to follow its face
 * (the user, 2026-11-01): `countertop` with `polygonCorners` — its plan
 * outline less what lies past the line of the angled face moved out by the
 * counter's own front overhang there. The countertop unchanged when it does
 * not cover the cut (a counter elsewhere on the run, a wall angled end).
 */
function cutCounterAtAngledEnd(countertop, cabinet) {
  const cut = angledEndCut(cabinet);
  if (!cut || tierOf(cabinet) !== 'base' || !countertop?.dimensions)
    return countertop;
  // the counter's front past the cabinet's face, read in the cabinet's frame
  const ry = cabinet.rotation?.y || 0;
  const facing = { x: Math.sin(ry), z: Math.cos(ry) };
  const along = { x: Math.cos(ry), z: -Math.sin(ry) };
  const rel = (p) => ({
    x: (p.x - cabinet.center.x) * along.x + (p.z - cabinet.center.z) * along.z,
    z:
      (p.x - cabinet.center.x) * facing.x + (p.z - cabinet.center.z) * facing.z,
  });
  const corners = planCorners(countertop);
  const local = corners.map(rel);
  const d = cabinet.dimensions.z;
  const w = cabinet.dimensions.x;
  // only a counter over this cabinet's cut corner
  const covers = local.some(
    (p) =>
      p.z > d / 2 - cut.cutM &&
      (cut.side === 'right' ? p.x > w / 2 - cut.cutM : p.x < -w / 2 + cut.cutM)
  );
  if (!covers) return countertop;
  const overhang = Math.max(0, Math.max(...local.map((p) => p.z)) - d / 2);
  const offsetOf = (p) =>
    (p.x - cut.front.x) * cut.normal.x +
    (p.z - cut.front.z) * cut.normal.z -
    overhang;
  if (!local.some((p) => offsetOf(p) > 1e-9)) return countertop;
  const kept = clipToHalfPlane(local, offsetOf);
  if (kept.length < 3) return countertop;
  return {
    ...countertop,
    polygonCorners: kept.map((p) => toWorld(cabinet, p)),
  };
}

module.exports = {
  ANGLED_ENDS,
  isAngledEnd,
  angledEndReason,
  stampAngledEndSides,
  angledEndCut,
  angledEndLocalOutline,
  angledEndInset,
  angledEndOutline,
  cutCounterAtAngledEnd,
};
